import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const MfaVerificationPrompt = ({ onVerify, onCancel, isLoading, error }) => {
  const [totpCode, setTotpCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (totpCode.length >= 6) {
      onVerify(totpCode, trustDevice);
    }
  };

  const toggleBackupMode = () => {
    setUseBackupCode((prev) => !prev);
    setTotpCode('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-600/20 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-white mb-1">Authentification à deux facteurs</h3>
        <div className="flex flex-col gap-2">
          <p className="text-gray-400 text-sm">
            {useBackupCode
              ? 'Entrez votre code de secours MFA.'
              : 'Entrez le code à 6 chiffres depuis votre application d’authentification.'}
          </p>
          <button
            type="button"
            onClick={toggleBackupMode}
            className="self-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-blue-200 hover:bg-white/10 transition-colors"
          >
            {useBackupCode ? 'Utiliser un code TOTP' : 'J’utilise un code de secours'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            ref={inputRef}
            type="text"
            value={totpCode}
            onChange={(e) => setTotpCode(
              e.target.value
                .toUpperCase()
                .replace(useBackupCode ? /[^A-Z0-9]/g : /\D/g, '')
                .slice(0, useBackupCode ? 16 : 6)
            )}
            placeholder={useBackupCode ? 'CODE1234' : '000000'}
            disabled={isLoading}
            className="w-full py-4 px-4 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-center text-2xl tracking-[0.5em] font-mono"
            maxLength={useBackupCode ? 16 : 6}
            autoComplete="one-time-code"
            inputMode={useBackupCode ? 'text' : 'numeric'}
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-400">Ne pas redemander sur cet appareil pendant 30 jours</span>
        </label>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-3 rounded-lg font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading || totpCode.length < 6}
            className={`flex-1 py-3 rounded-lg font-semibold text-white transition-all ${
              isLoading || totpCode.length < 6
                ? 'bg-blue-600/50 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500'
            }`}
          >
            {isLoading ? (
              <svg className="animate-spin w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : 'Vérifier'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default MfaVerificationPrompt;
